<script setup>
import { ref, onMounted } from 'vue'
import { hybrid } from '@dpgradio/creative'
import { useGameStore } from './stores/game'
import ScoreBoard from './components/ScoreBoard.ce.vue'
import InteractiveMap from './components/InteractiveMap.ce.vue'
import sampleData from './sample-data'

const props = defineProps({
  fullscreen: { type: Boolean, default: false },
})

const isNativeApp = hybrid.isNativeApp()

const USE_SAMPLE_DATA = false

const SPREADSHEET_ID = '1PwfpQ8EhBGR-jDcUYFZI3eSkRHdeAPWMLJj1ha86iH0'
const BASE_URL = 'https://spreadsheet.radio.dpgmedia.cloud/prod/fetch'

function fetchSheet(sheet) {
  const params = new URLSearchParams({
    provider: 'google',
    spreadsheetId: SPREADSHEET_ID,
    range: `${sheet}!A:Z`,
  })
  return fetch(`${BASE_URL}?${params}`).then((r) => r.json())
}

function parseRow(row, type) {
  return {
    ...row,
    lat: parseFloat(row.lat),
    lng: parseFloat(row.lng),
    type,
  }
}

const store = useGameStore()
const loading = ref(true)

onMounted(async () => {
  if (USE_SAMPLE_DATA) {
    store.loadData(sampleData)
    loading.value = false
    return
  }

  const [challenges, searches, players] = await Promise.all([
    fetchSheet('challenges'),
    fetchSheet('searches'),
    fetchSheet('players'),
  ])

  store.loadData({
    challenges: challenges.map((r) => parseRow(r, 'challenge')),
    searches: searches.map((r) => parseRow(r, 'search')),
    players,
  })
  loading.value = false
})
</script>

<template>
  <div class="app-layout" :class="{ fullscreen: props.fullscreen, 'native-app': isNativeApp }">
    <div v-if="loading" class="loading">
      <div class="loading-spinner" />
    </div>
    <template v-else>
      <ScoreBoard />
      <InteractiveMap />
    </template>
  </div>
</template>

<style>
@import '@dpgradio/creative/styles/all';

:host {
  --q-red: 237 54 36;
  --font-heading: 'Cervo', sans-serif;
  --font-body: 'Qarla', sans-serif;
  display: block;
  font-family: var(--font-body);
  color: #fff;
  height: 100%;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app-layout {
  display: flex;
  min-height: 400px;
  height: 100%;
  flex-direction: column;
  background: oklch(from rgb(var(--q-red)) calc(l - 0.12) c h);
  position: relative;
  overflow: hidden;
}

.app-layout.fullscreen {
  min-height: 100vh;
  min-height: 100svh;
}

.app-layout.native-app {
  padding-bottom: 100px;
}

/* Sunbeams overlay */
.app-layout::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: repeating-conic-gradient(from 0deg at 50% 50%, rgba(0, 0, 0, 0.06) 0deg 8deg, transparent 8deg 16deg);
  pointer-events: none;
  z-index: 1;
  animation: rotate-sunbeams 200s linear infinite;
  will-change: transform;
}

@keyframes rotate-sunbeams {
  to {
    transform: rotate(360deg);
  }
}

.loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Texture overlay */
.app-layout::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('./assets/texture.webp') repeat;
  mix-blend-mode: multiply;
  pointer-events: none;
  z-index: 1;
  opacity: 1;
}
</style>
